import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { StepMessage } from '../chatMessage'
import AssistantStepEntry, { iconByStatus } from '../AssistantStepEntry'

export type AssistantStepGroupProps = {
  steps: StepMessage[]
}

/**
 * Collapses a run of DOM-tool step messages into a single MUI Accordion.
 * The summary (bottom row) shows the last step; expanding reveals earlier steps above it.
 * A single-step group renders as a plain entry with no accordion chrome.
 */
const AssistantStepGroup = ({ steps }: AssistantStepGroupProps) => {
  const lastStep = steps[steps.length - 1]
  const status = lastStep.stepStatus
  const earlierSteps = steps.slice(0, -1)

  if (earlierSteps.length === 0) {
    return <AssistantStepEntry text={lastStep.text} stepStatus={status} />
  }

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        background: 'transparent',
        '&:before': { display: 'none' },
        display: 'flex',
        flexDirection: 'column-reverse'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
        sx={{
          px: 1,
          minHeight: 'unset',
          '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 0.75 }
        }}
      >
        {iconByStatus[status]}
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {lastStep.text}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, py: 0 }}>
        {earlierSteps.map((step) => (
          <AssistantStepEntry key={step.id} text={step.text} stepStatus={step.stepStatus} />
        ))}
      </AccordionDetails>
    </Accordion>
  )
}

export default AssistantStepGroup
